import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { RxState } from '@rx-angular/state';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyDataSource, GalaxyTableModule, Row } from '@vendasta/galaxy/table';
import { GalaxyColumnDef } from '@vendasta/galaxy/table/src/table.interface';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { ActivityType, ObjectType, SelectAllAction } from '../../tokens-and-interfaces';
import { LIST_OBJECTS_DEFAULT_PAGE_SIZE, LIST_OBJECTS_PAGE_SIZE_OPTIONS, PAGE_ROUTES } from '../../constants';
import { TranslationModule } from '../../i18n/translation-module';
import { DeleteModalComponent } from './delete-modal/delete-modal.component';
import { ListObjectsTableService } from './list-objects-table.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { TranslateByObjectTypePipe } from '../../i18n/translate-by-object-type/translate-by-object-type.pipe';
import { TranslateByObjectTypeService } from '../../i18n/translate-by-object-type/translate-by-object-type.service';
import { SidepanelComponent } from '../side-panel/side-panel.component';
import { FilterVisibleActionsPipe } from '../../shared-pipes';
import { CrmTableStateService } from '../../shared-services/crm-services/crm-object-service/crm-table-state.service';
import { GalaxyFilterChipsModule, GalaxyFilterInterface } from '@vendasta/galaxy/filter/chips';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { toObservable } from '@angular/core/rxjs-interop';
import { CrmFiltersService } from '../../shared-services/crm-services/crm-filters.service';
import { decodeFilters, encodeFilters, validateFilters } from '@vendasta/galaxy/filter/chips/src/utils';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { FilterViewComponent } from '../filter-view';
import { GalaxyFilterChipsComponent } from '@vendasta/galaxy/filter/chips/src/galaxy-filter-chips.component';
import {
  CrmInjectionToken,
  CrmDependencies,
  CRMRowObject,
  MultiRowAction,
  SingleRowAction,
} from '../../tokens-and-interfaces';
import { CustomRowStyle } from '../../shared-services/table-customization/_abstract-table-customization.service';

@Component({
  selector: 'crm-list-objects-table',
  templateUrl: `./list-objects-table.component.html`,
  styleUrls: ['./list-objects-table.component.scss'],
  imports: [
    CommonModule,
    GalaxyEmptyStateModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTableModule,
    MatTableModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    TranslationModule,
    MatDialogModule,
    GalaxySnackbarModule,
    GalaxyBadgeModule,
    GalaxyPageModule,
    TranslateByObjectTypePipe,
    SidepanelComponent,
    FilterVisibleActionsPipe,
    GalaxyFilterChipsModule,
    MatSortModule,
    FilterViewComponent,
  ],
  providers: [ListObjectsTableService, CrmTableStateService, RxState],
  standalone: true,
})
export class ListObjectsTableComponent implements OnInit, OnDestroy {
  @Input() rowStyle: CustomRowStyle | undefined;

  private _objectType = signal<ObjectType>('Contact');
  @Input({ required: true }) set objectType(o: ObjectType) {
    this._objectType.set(o);
    this.tableStateService.setLocalStorageKey(this._objectType());
    this.service.setObjectType(this._objectType());
  }

  get objectType(): ObjectType {
    return this._objectType();
  }

  get objectTypeForTranslation(): ObjectType | ActivityType {
    return this.activityType ?? this.objectType;
  }

  multiRowActions$: Observable<MultiRowAction[]> = toObservable(this._objectType).pipe(
    switchMap((objectType) => {
      return this.createMultiRowActions(objectType);
    }),
  );

  selectAllActions$: Observable<SelectAllAction[]> = toObservable(this._objectType).pipe(
    switchMap((objectType) => {
      return this.createSelectAllActions(objectType);
    }),
  );

  singleRowActions$: Observable<SingleRowAction[]> = toObservable(this._objectType).pipe(
    switchMap((objectType) => {
      return this.createSingleRowActions(objectType);
    }),
  );
  multiRowActionExists$: Observable<boolean> = this.multiRowActions$.pipe(map((actions) => actions.length > 0));

  private _activityType: ActivityType | undefined;
  @Input() set activityType(o: ActivityType | undefined) {
    if (o) {
      this._activityType = o;
      this.service.setActivityType(this._activityType);
    }
  }

  get activityType(): ActivityType | undefined {
    return this._activityType;
  }

  @Output() editClick: EventEmitter<CRMRowObject> = new EventEmitter<CRMRowObject>();
  @ViewChild('filterChipRef') filterChipRef?: GalaxyFilterChipsComponent;

  dataSource: GalaxyDataSource<Row, GalaxyFilterInterface, MatSort> = this.service.dataSource;
  columns$: Observable<GalaxyColumnDef[]> = this.service.columns$;
  groupConfigs$: Observable<GalaxyColumnDef[]> = this.service.groupConfigs$;
  initialLoading$: Observable<boolean> = this.service.initialLoading$;
  showEmptyState$: Observable<boolean> = this.service.showEmptyState$;
  showObjects$: Observable<boolean> = this.service.showObjects$;
  selectedRows: Row[] = [];
  showFiltersApplied$: Observable<boolean> = this.dataSource.filtersApplied$;
  protected readonly filters = signal<GalaxyFilterInterface[]>([]);

  readonly data$ = this.service.data$;

  pageSizeOptions: number[] = LIST_OBJECTS_PAGE_SIZE_OPTIONS;
  pageSize: number = LIST_OBJECTS_DEFAULT_PAGE_SIZE;

  readonly isMobile$ = this.tableStateService.isMobile$;

  viewKeys: string[] = [];

  private readonly confirmationModalService = inject(OpenConfirmationModalService);

  tableId = '';

  private subscriptions: Subscription[] = [];

  useSelectAll$: Observable<boolean> = this.dataSource.selectAll$ ?? of(false);

  hasSelectAllActions$: Observable<boolean> = this.selectAllActions$.pipe(map((actions) => actions.length > 0));

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly service: ListObjectsTableService,
    private readonly router: Router,
    private readonly snackService: SnackbarService,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
    public dialog: MatDialog,
    private readonly tableStateService: CrmTableStateService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly crmFiltersService: CrmFiltersService,
  ) {
    this.dataSource.clearSelectionOnChange = true;
    const filterParam = this.activatedRoute.snapshot.queryParamMap.get('filter');
    let filters: GalaxyFilterInterface[] = [];
    if (filterParam) {
      const f = decodeFilters(filterParam);
      if (validateFilters(f) && f.length > 0) {
        filters = f;
      }
    }
    this.crmFiltersService.setInitialFilters(filters);

    const sub = combineLatest([this.activatedRoute.queryParams, this.dataSource.state$])
      .pipe(
        tap(([queryParams, state]) => {
          const newQueryParams = { ...queryParams };
          let filterParam: string | null = null;
          if (state.filters && state.filters.length > 0) {
            filterParam = encodeFilters(state.filters);
            newQueryParams['filter'] = filterParam;
          } else {
            delete newQueryParams['filter'];
          }
          // Note: `queryParamsHandling: 'merge'` doesn't work when multiple shared-components are adjusting the query params
          // at the same time. Without this other shared-components may not be able to write to the query params.
          if (queryParams['filter'] === newQueryParams['filter']) {
            return;
          }
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: newQueryParams,
          });
        }),
      )
      .subscribe();
    this.subscriptions.push(sub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnInit() {
    switch (this.objectType) {
      case 'Contact':
        if (this.config.contact?.baseColumnIds && this.config.contact.baseColumnIds.length > 0) {
          this.service.setBaseColumns(this.config.contact.baseColumnIds);
        }
        break;
      case 'Company':
        if (this.config.company?.baseColumnIds && this.config.company.baseColumnIds.length > 0) {
          this.service.setBaseColumns(this.config.company.baseColumnIds);
        }
        break;
      case 'Activity':
        switch (this.activityType) {
          case 'Task':
            if (this.config.task?.baseColumnIds && this.config.task.baseColumnIds.length > 0) {
              this.service.setBaseColumns(this.config.task.baseColumnIds);
            }
            break;
        }
    }

    const viewKeys: string[] = [];
    if (this.config.appID) {
      viewKeys.push('crm:' + this.config.appID + ':list-' + this.objectType + ':table');
    }
    viewKeys.push('crm:list-' + this.objectType + ':table');
    this.viewKeys = viewKeys;

    this.tableId = this.config.appID + '-' + this.objectType;
  }

  editObject(objectId: string): void {
    this.editClick.emit({ objectId: objectId, objectType: this.objectType });
    this.config.routePrefix$.subscribe((routePrefix) => {
      let editRouteTemplate;
      switch (this.objectType) {
        case 'Contact':
          editRouteTemplate = `${PAGE_ROUTES.CONTACT.ROOT}/${PAGE_ROUTES.CONTACT.SUBROUTES.EDIT}`;
          break;
        case 'Company':
          editRouteTemplate = `${PAGE_ROUTES.COMPANY.ROOT}/${PAGE_ROUTES.COMPANY.SUBROUTES.EDIT}`;
          break;
      }
      if (editRouteTemplate) {
        const editRoute = editRouteTemplate.replace(':crmObjectId', objectId);
        this.router.navigateByUrl(`${routePrefix}/${editRoute}`);
      }
    });
  }

  deleteObject(row: Row): void {
    this.dialog
      .open(DeleteModalComponent, {
        width: '560px',
        data: {
          objectType: this.objectTypeForTranslation,
          id: row.id,
          name$: this.service.lookupRowDisplayName(row.id),
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.service.deleteObject(row.id).subscribe({
            next: () => {
              const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
                this.objectTypeForTranslation,
                'LIST_OBJECTS_TABLE.ACTIONS.DELETE.SUCCESS',
              );
              this.snackService.openSuccessSnack(translatedString);
            },
            error: (err) => {
              console.error(err);
              const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
                this.objectTypeForTranslation,
                'LIST_OBJECTS_TABLE.ACTIONS.DELETE.ERROR',
              );
              this.snackService.openErrorSnack(translatedString);
            },
          });
        }
      });
  }

  updateFilters(filters: GalaxyFilterInterface[], updateChipList?: boolean): void {
    this.service.filters = filters;
    this.dataSource.setFilters(filters);
    this.filters.set(filters);
    if (updateChipList) {
      this.filterChipRef?.filters.set(filters);
    }
  }

  selectionChanged(rows: Row[]): void {
    this.selectedRows = rows;
  }

  columnsChanged(columns: GalaxyColumnDef[]): void {
    // there's a select column at the start of every table execution with an advanced selector
    // the columns shouldn't be saved if only the select column is available
    // in the list of columns
    const columnCount = columns.filter((c) => c.id !== 'select').length;
    if (columnCount > 0) {
      this.tableStateService.setLocalStorageColumns(columns);
    }
  }

  private createSingleRowActions(objectType: ObjectType): Observable<SingleRowAction[]> {
    const commonSingleRowActions$ = this.config.singleRowTableActions$ ?? of([]);
    let singleRowActions$: Observable<SingleRowAction[]>;

    switch (objectType) {
      case 'Contact':
        singleRowActions$ = this.config.contact?.singleRowTableActions$ ?? of([]);
        break;
      case 'Company':
        singleRowActions$ = this.config.company?.singleRowTableActions$ ?? of([]);
        break;
      case 'Activity':
        singleRowActions$ = this.config.task?.singleRowTableActions$ ?? of([]);
        break;
      default:
        singleRowActions$ = of([]);
        break;
    }

    return combineLatest([singleRowActions$, commonSingleRowActions$]).pipe(
      map(([typeActions, commonActions]) => [...typeActions, ...commonActions]),
    );
  }

  deleteRows(rows: Row[]): void {
    this.confirmationModalService
      .openModal({
        type: 'warn',
        title: this.translateByObjectTypeService.getTranslationByObjectType(
          this.objectTypeForTranslation,
          'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.TITLE',
        ),
        message: this.translateByObjectTypeService.getTranslationByObjectType(
          this.objectTypeForTranslation,
          'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CONFIRMATION_MESSAGE',
        ),
        confirmButtonText: this.translateByObjectTypeService.getTranslationByObjectType(
          this.objectTypeForTranslation,
          'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CONFIRM',
        ),
        cancelButtonText: this.translateByObjectTypeService.getTranslationByObjectType(
          this.objectTypeForTranslation,
          'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.CANCEL',
        ),
      })
      .subscribe((confirmed) => {
        if (confirmed) {
          const ids = rows.map((row) => row.id);
          forkJoin(
            ids.map((id) => {
              return this.service.deleteObject(id).pipe(
                map(() => ''),
                catchError(() => {
                  return of(id);
                }),
              );
            }),
          ).subscribe((resp: string[]) => {
            const failedIDs = resp.filter((address) => address !== '');
            if (failedIDs.length === 0) {
              const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
                this.objectTypeForTranslation,
                'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.SUCCESS',
              );
              this.snackService.openSuccessSnack(translatedString);
            } else {
              console.error('Failed to delete IDs: ', failedIDs);
              const translatedString = this.translateByObjectTypeService.getTranslationByObjectType(
                this.objectTypeForTranslation,
                'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.ERROR',
              );
              this.snackService.openErrorSnack(translatedString);
            }
            if (resp.length - failedIDs.length > 0) {
              // If anything has been deleted, clear the selection
              this.dataSource.clearSelection();
            }
          });
        }
      });
  }

  private createMultiRowActions(objectType: ObjectType): Observable<MultiRowAction[]> {
    const commonMultiRowActions$ = this.config.multiRowTableActions$ ?? of([]);
    let multiRowActions$: Observable<MultiRowAction[]>;

    switch (objectType) {
      case 'Contact':
        multiRowActions$ = this.config.contact?.multiRowTableActions$ ?? of([]);
        break;
      case 'Company':
        multiRowActions$ = this.config.company?.multiRowTableActions$ ?? of([]);
        break;
      case 'Activity':
        multiRowActions$ = this.config.task?.multiRowTableActions$ ?? of([]);
        break;
      default:
        multiRowActions$ = of([]);
        break;
    }

    const deleteRowsAction = {
      label: this.translateByObjectTypeService.getTranslationByObjectType(
        this.objectTypeForTranslation,
        'LIST_OBJECTS_TABLE.ACTIONS.DELETE_SELECTED.ACTION',
      ),
      callback: (rows: Row[]) => this.deleteRows(rows),
      selectAllVisible: false,
    };

    return combineLatest([multiRowActions$, commonMultiRowActions$]).pipe(
      map(([typeActions, commonActions]) => [...typeActions, ...commonActions, deleteRowsAction]),
    );
  }

  private createSelectAllActions(objectType: ObjectType): Observable<SelectAllAction[]> {
    const commonSelectAllActions$ = this.config.selectAllActions$ ?? of([]);
    let selectAllActions$: Observable<SelectAllAction[]>;

    switch (objectType) {
      case 'Contact':
        selectAllActions$ = this.config.contact?.selectAllTableActions$ ?? of([]);
        break;
      case 'Company':
        selectAllActions$ = this.config.company?.selectAllTableActions$ ?? of([]);
        break;
      case 'Activity':
        selectAllActions$ = this.config.task?.selectAllTableActions$ ?? of([]);
        break;
      default:
        selectAllActions$ = of([]);
        break;
    }

    return combineLatest([selectAllActions$, commonSelectAllActions$]).pipe(
      map(([typeActions, commonActions]) => [...typeActions, ...commonActions]),
    );
  }

  handleSelectAll() {
    this.dataSource.toggleSelectAll();
  }
}
